import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, TrendingUp, TrendingDown, DollarSign, Target, AlertTriangle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, LineChart, Line, ComposedChart, Area } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVMDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [evmData, setEvmData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchData(selectedProject);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].project_id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchData = async (projectId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/evm`);
      setEvmData(response.data);
    } catch (error) {
      console.error('Error fetching EVM data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (Math.abs(value) >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (Math.abs(value) >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value}`;
  };

  const getStatusColor = (value, isPositiveGood = true) => {
    if (isPositiveGood) {
      return value >= 0 ? 'text-green-600' : 'text-red-600';
    }
    return value <= 0 ? 'text-green-600' : 'text-red-600';
  };

  const getIndexColor = (value) => {
    if (value >= 1) return 'text-green-600 bg-green-50';
    if (value >= 0.9) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  if (loading && !evmData) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-12 h-12 border-3 border-gray-200 border-t-purple-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  const summary = evmData?.summary || {};

  return (
    <div className="pb-20 bg-gray-50 min-h-screen" data-testid="evm-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate('/')} className="p-1">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">{t('evm.title')}</h1>
            <p className="text-xs text-gray-500">{t('evm.subtitle')}</p>
          </div>
        </div>
        
        <Select value={selectedProject} onValueChange={setSelectedProject}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('common.selectProject')} />
          </SelectTrigger>
          <SelectContent>
            {projects.map((project) => (
              <SelectItem key={project.project_id} value={project.project_id}>
                {project.project_code} - {project.project_name.substring(0, 25)}...
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Key Metrics Summary */}
        <Card className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
          <CardContent className="p-4">
            <h3 className="text-sm font-medium opacity-80 mb-3">{t('evm.summary')}</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <p className="text-xs opacity-70">PV (BCWS)</p>
                <p className="text-lg font-bold">{formatCurrency(summary.pv)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs opacity-70">EV (BCWP)</p>
                <p className="text-lg font-bold">{formatCurrency(summary.ev)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs opacity-70">AC (ACWP)</p>
                <p className="text-lg font-bold">{formatCurrency(summary.ac)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Performance Indices */}
        <div className="grid grid-cols-2 gap-3">
          <Card className={getIndexColor(summary.spi)}>
            <CardContent className="p-3 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                {summary.spi >= 1 ? (
                  <CheckCircle size={16} className="text-green-600" />
                ) : (
                  <AlertTriangle size={16} className="text-current" />
                )}
                <span className="text-xs font-medium">SPI</span>
              </div>
              <p className="text-2xl font-bold">{summary.spi}</p>
              <p className="text-xs mt-1">
                {summary.spi >= 1 ? t('evm.onSchedule') : summary.spi >= 0.9 ? t('evm.slightDelay') : t('evm.behindSchedule')}
              </p>
            </CardContent>
          </Card>

          <Card className={getIndexColor(summary.cpi)}>
            <CardContent className="p-3 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                {summary.cpi >= 1 ? (
                  <CheckCircle size={16} className="text-green-600" />
                ) : (
                  <AlertTriangle size={16} className="text-current" />
                )}
                <span className="text-xs font-medium">CPI</span>
              </div>
              <p className="text-2xl font-bold">{summary.cpi}</p>
              <p className="text-xs mt-1">
                {summary.cpi >= 1 ? t('evm.underBudget') : summary.cpi >= 0.9 ? t('evm.slightOverrun') : t('evm.overBudget')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Variances */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('evm.varianceAnalysis')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded bg-gray-50">
                <p className="text-xs text-gray-500">{t('evm.scheduleVariance')}</p>
                <p className={`text-lg font-bold ${getStatusColor(summary.sv)}`}>
                  {formatCurrency(summary.sv)}
                </p>
                <p className="text-xs text-gray-400 mt-1">{t('evm.svFormula')}</p>
              </div>
              <div className="p-3 rounded bg-gray-50">
                <p className="text-xs text-gray-500">{t('evm.costVariance')}</p>
                <p className={`text-lg font-bold ${getStatusColor(summary.cv)}`}>
                  {formatCurrency(summary.cv)}
                </p>
                <p className="text-xs text-gray-400 mt-1">{t('evm.cvFormula')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Forecasts */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('evm.costForecast')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                <div>
                  <p className="text-xs text-gray-500">{t('evm.bac')}</p>
                  <p className="text-sm font-bold text-blue-600">{formatCurrency(summary.bac)}</p>
                </div>
                <Target size={20} className="text-blue-400" />
              </div>
              
              <div className="flex justify-between items-center p-2 bg-purple-50 rounded">
                <div>
                  <p className="text-xs text-gray-500">{t('evm.eac')}</p>
                  <p className="text-sm font-bold text-purple-600">{formatCurrency(summary.eac)}</p>
                </div>
                <TrendingUp size={20} className="text-purple-400" />
              </div>
              
              <div className="flex justify-between items-center p-2 bg-amber-50 rounded">
                <div>
                  <p className="text-xs text-gray-500">{t('evm.etc')}</p>
                  <p className="text-sm font-bold text-amber-600">{formatCurrency(summary.etc)}</p>
                </div>
                <DollarSign size={20} className="text-amber-400" />
              </div>
              
              <div className={`flex justify-between items-center p-2 rounded ${summary.vac >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                <div>
                  <p className="text-xs text-gray-500">{t('evm.vac')}</p>
                  <p className={`text-sm font-bold ${getStatusColor(summary.vac)}`}>{formatCurrency(summary.vac)}</p>
                </div>
                {summary.vac >= 0 ? (
                  <TrendingDown size={20} className="text-green-400" />
                ) : (
                  <TrendingUp size={20} className="text-red-400" />
                )}
              </div>
              
              <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                <div>
                  <p className="text-xs text-gray-500">To Complete Performance Index (TCPI)</p>
                  <p className={`text-sm font-bold ${summary.tcpi <= 1 ? 'text-green-600' : 'text-red-600'}`}>{summary.tcpi}</p>
                </div>
                <AlertTriangle size={20} className={summary.tcpi <= 1 ? 'text-green-400' : 'text-red-400'} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* PV/EV/AC Trend Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('evm.trendAnalysis')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={evmData?.monthly || []} margin={{ left: 0, right: 10 }}>
                  <XAxis dataKey="month" tick={{ fontSize: 8 }} angle={-45} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => formatCurrency(v)} />
                  <Tooltip 
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-white p-2 shadow-lg rounded border text-xs">
                            <p className="font-semibold mb-1">{label}</p>
                            {payload.map((entry, index) => (
                              <p key={index} style={{ color: entry.color }}>
                                {entry.name}: {formatCurrency(entry.value)}
                              </p>
                            ))}
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '10px' }} />
                  <Line type="monotone" dataKey="pv" name={t('evm.legendPV')} stroke="#3b82f6" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="ev" name={t('evm.legendEV')} stroke="#22c55e" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="ac" name={t('evm.legendAC')} stroke="#ef4444" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Discipline Breakdown */}
        {evmData?.disciplines && evmData.disciplines.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t('evm.byDiscipline')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={evmData.disciplines} layout="vertical" margin={{ left: 0, right: 10 }}>
                    <XAxis type="number" tick={{ fontSize: 9 }} tickFormatter={(v) => formatCurrency(v)} />
                    <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 9 }} />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white p-2 shadow-lg rounded border text-xs">
                              <p className="font-semibold mb-1">{data.name}</p>
                              <p className="text-blue-600">PV: {formatCurrency(data.pv)}</p>
                              <p className="text-green-600">EV: {formatCurrency(data.ev)}</p>
                              <p className="text-red-600">AC: {formatCurrency(data.ac)}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '9px' }} />
                    <Bar dataKey="pv" name="PV" fill="#93c5fd" />
                    <Bar dataKey="ev" name="EV" fill="#86efac" />
                    <Bar dataKey="ac" name="AC" fill="#fca5a5" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* EVM Terminology */}
        <Card>
          <CardContent className="p-4">
            <h4 className="font-medium text-sm text-gray-900 mb-3">{t('evm.terminology')}</h4>
            <div className="space-y-2 text-xs text-gray-600">
              <p><strong>PV (BCWS):</strong> {t('evm.termPV')}</p>
              <p><strong>EV (BCWP):</strong> {t('evm.termEV')}</p>
              <p><strong>AC (ACWP):</strong> {t('evm.termAC')}</p>
              <p><strong>SPI:</strong> {t('evm.termSPI')}</p>
              <p><strong>CPI:</strong> {t('evm.termCPI')}</p>
              <p><strong>EAC:</strong> {t('evm.termEAC')}</p>
              <p><strong>TCPI:</strong> {t('evm.termTCPI')}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EVMDashboard;
